import Filter from "./Filter";
import Sorter from "./Sorter";
import * as React from "react";
import Loading from "./Loading";
import LoadMore from "./LoadMore";
import DealsShown from "./DealsShown";
import { useRouter } from "next/router";
import Deal from "./Deals/BroadbandDeal";
import DealsNotFound from "./DealsNotFound";
import MobileFilter from "./Filter/MobileFilter";
import { createDealClick } from "@/api/tracking";
import { UtmContext } from "@/context/utmContext";
import { KeywordContext } from "@/context/keyword";
import BroadbandLayout from "./Layouts/BroadbandLayout";
import { BroadbandComparisonType, Product } from "@/types/broadbandProduct";

const Comparison: React.FC<{
  defaultSort?:
    | "recommended"
    | "price-high-low"
    | "price-low-high"
    | "speed-high-low"
    | "speed-low-high";
  products: Array<Product>;
  hideSpeedsFilter?: Boolean;
  hideContractsFilter?: Boolean;
  hideProvidersFilter?: Boolean;
  broadbandComparison: BroadbandComparisonType;
}> = ({
  defaultSort = "recommended",
  products,
  hideSpeedsFilter = false,
  hideContractsFilter = false,
  hideProvidersFilter = false,
  broadbandComparison,
}) => {
  const router = useRouter();
  const { sclid, gclid, fbclid, msclkid, tbclid, obclid } =
    React.useContext(KeywordContext);
  const { utmContent, utmCampaign, utmSource, utmMedium, utmTerm } =
    React.useContext(UtmContext);

  // Whenever the products change we need to update
  // the local state for viewableDeals and totalDeals.
  React.useEffect(() => {
    console.log("useEffect1");
    broadbandComparison.setTotalDeals(products.length);

    if (broadbandComparison.viewableDeals > products.length) {
      broadbandComparison.setViewableDeals(products.length);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, broadbandComparison.viewableDeals]);

  // Click handler for incrementing
  // the viewableDeals const.
  const incrementViewableDeals = () => {
    console.log("incrementViewableDeals");

    if (
      broadbandComparison.viewableDeals + 10 >
      broadbandComparison.totalDeals
    ) {
      broadbandComparison.setViewableDeals(broadbandComparison.totalDeals);
      return;
    }
    broadbandComparison.setViewableDeals(
      broadbandComparison.viewableDeals + 10
    );
  };

  if (
    broadbandComparison.totalDeals !== 0 &&
    broadbandComparison.viewableDeals < 10
  ) {
    incrementViewableDeals();
  }

  // Only render {viewableDeals} number of products.
  const deals = products
    .slice(0, broadbandComparison.viewableDeals)
    .map(function (deal, index) {
      const onDealClick = async (product: Product) => {
        createDealClick({
          type: "broadband",
          click_reference:
            sclid ??
            gclid ??
            fbclid ??
            msclkid ??
            tbclid ??
            obclid ??
            utmContent,
          utm_campaign: utmCampaign,
          utm_content: utmContent,
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_term: utmTerm,
          product: product.name,
          provider: product.network.name,
          metadata: {
            dealPosition: index + 1,
            page: router.isReady ? router.asPath : "",
            monthlyCost: product.monthly_cost,
            upfrontCost: product.setup_fee,
            contractLength: product.contract_length,
            downloadSpeeds: product.download_speed,
            isFibre: product.fibre,
            offerExpiry: product.offer_expiry,
            offerExtras: product.extras.map((extra) => extra.name),
          },
        });
      };

      return (
        <div className="col-12 text-start" key={index}>
          <Deal
            data={deal}
            urlReference={
              sclid ??
              gclid ??
              fbclid ??
              msclkid ??
              tbclid ??
              obclid ??
              utmContent
            }
            urlReference2={utmCampaign}
            urlReference3={utmSource}
            onDealClick={onDealClick}
          />
        </div>
      );
    });

  const getFilterProps = () => {
    const filterProps = [];
    if (!hideContractsFilter) {
      filterProps.push({
        title: "Contracts",
        filters: broadbandComparison.contractFilters,
        toggleFilter: broadbandComparison.toggleContractFilter,
      });
    }
    if (!hideSpeedsFilter) {
      filterProps.push({
        title: "Speeds",
        filters: broadbandComparison.speedFilters,
        toggleFilter: broadbandComparison.toggleSpeedFilter,
      });
    }
    if (!hideProvidersFilter) {
      filterProps.push({
        title: "Providers",
        filters: broadbandComparison.networkFilters,
        toggleFilter: broadbandComparison.toggleNetworkFilter,
      });
    }
    return filterProps;
  };

  return (
    <BroadbandLayout
      desktopFilters={<Filter filterProps={getFilterProps() || false} />}
      dealsShown={
        <DealsShown
          viewableDeals={broadbandComparison.viewableDeals}
          totalDeals={broadbandComparison.totalDeals}
        />
      }
      sorter={
        <Sorter
          defaultSort={defaultSort}
          sortPropsVariant={"broadband"}
          setSorter={broadbandComparison.setSorter}
        />
      }
      mobileFilters={
        broadbandComparison.totalDeals === 0 ? null : (
          <MobileFilter filterProps={getFilterProps() || false} />
        )
      }
      comparisonTable={
        broadbandComparison.loading ? (
          <Loading />
        ) : broadbandComparison.totalDeals > 0 ? (
          deals
        ) : (
          <DealsNotFound clearFilters={broadbandComparison.clearFilters} />
        )
      }
      loadMore={
        broadbandComparison.totalDeals > broadbandComparison.viewableDeals &&
        !broadbandComparison.loading ? (
          <LoadMore onClick={incrementViewableDeals} />
        ) : null
      }
    />
  );
};

export default Comparison;
